import React, { useState, useRef, useEffect } from 'react';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm the Jnanakshi Solutions assistant. Choose a question below to get started:",
      isBot: true,
      timestamp: new Date()
    }
  ]);
  const messagesEndRef = useRef(null);

  // Static Q&A data - 10 questions
  const qaData = [
    {
      question: "What services do you offer?",
      answer: "We offer live workshops, exclusive training programs, and guidance & mentoring. Our services include Python, Java, SQL training, soft skills workshops, software engineering with Agile, software testing, and even Yoga classes!"
    },
    {
      question: "What is Python with AI training?",
      answer: "Our Python with AI training covers machine learning fundamentals, data science with Pandas & NumPy, and AI model deployment. We start with Linear Regression and help you build real-world AI projects."
    },
    {
      question: "Do you offer no-code development?",
      answer: "Yes! We provide no-code/low-code development training using tools like Builder.io for React applications. This includes visual page building, drag-and-drop components, API integration, and rapid prototyping."
    },
    {
      question: "What about career guidance?",
      answer: "We offer comprehensive guidance & mentoring including internship guidance, career mentoring, and industry connections to help you become industry-ready."
    },
    {
      question: "Where are you located?",
      answer: "We are based in Bangalore, India. You can reach us at support@jnanakshi.com for any inquiries."
    },
    {
      question: "What makes Jnanakshi Solutions different?",
      answer: "We bridge the gap between academic knowledge and professional competence through immersive live workshops led by seasoned industry experts. Our focus is on making students industry-ready with hands-on skills."
    },
    {
      question: "Do you conduct workshops?",
      answer: "Yes! We conduct workshops on soft skills for all engineering branches, software engineering with Agile, Python, SQL, software testing, and even Yoga classes."
    },
    {
      question: "What programming languages do you teach?",
      answer: "We teach Python, Java, and SQL. Our Python training includes AI/ML development, our Java training covers enterprise development, and our SQL training focuses on database management and optimization."
    },
    {
      question: "How can I get started with your programs?",
      answer: "You can contact us at support@jnanakshi.com to discuss your learning goals. We'll help you choose the right program - whether it's workshops, exclusive training, or career guidance - based on your needs."
    },
    {
      question: "Do you offer internship opportunities?",
      answer: "Yes! We provide internship guidance and help connect students with industry opportunities. Our career mentoring includes assistance with internship applications, interview preparation, and maximizing learning experiences."
    }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleQuestionClick = (question, answer) => {
    const userMessage = {
      id: messages.length + 1,
      text: question,
      isBot: false,
      timestamp: new Date()
    };

    const botMessage = {
      id: messages.length + 2,
      text: answer,
      isBot: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage, botMessage]);
  };

  return (
    <div className="fixed bottom-3 right-3 sm:bottom-4 sm:right-4 z-50">
      {/* Chat Window */}
      {isOpen && (
        <div className="bg-white dark:bg-dark-700 rounded-lg shadow-2xl w-[calc(100vw-1.5rem)] sm:w-96 h-[calc(100vh-2rem)] sm:h-[500px] max-h-[600px] flex flex-col border border-gray-200 dark:border-dark-600">
          {/* Header */}
          <div className="bg-jnanakshi-500 text-white p-3 sm:p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-xs sm:text-sm">🤖</span>
              </div>
              <div>
                <h3 className="font-semibold text-xs sm:text-sm">Jnanakshi Assistant</h3>
                <p className="text-xs text-jnanakshi-100">Online</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-gray-200 transition-colors p-1"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2 sm:space-y-3 scrollbar-thin scrollbar-thumb-jnanakshi scrollbar-track-light dark:scrollbar-track-dark">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
              >
<div
  className={`max-w-[85%] sm:max-w-xs px-3 py-2 rounded-lg text-xs sm:text-sm ${
    message.isBot
      ? 'bg-gray-100 dark:bg-dark-600 text-gray-800 dark:text-gray-200 text-left'
      : 'bg-jnanakshi-500 text-white text-right'
  }`}
>
  {message.text}
</div>



              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Questions Section */}
          <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-dark-600">
            <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">Choose a question:</p>
            <div className="space-y-1 sm:space-y-2 max-h-32 sm:max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-jnanakshi scrollbar-track-light dark:scrollbar-track-dark">
              {qaData.map((qa, index) => (
                <button
                  key={index}
                  onClick={() => handleQuestionClick(qa.question, qa.answer)}
                  className="block w-full text-left text-xs sm:text-sm text-jnanakshi-600 dark:text-jnanakshi-400 hover:text-jnanakshi-700 dark:hover:text-jnanakshi-300 p-2 sm:p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors border border-gray-200 dark:border-dark-600 hover:border-jnanakshi-300 dark:hover:border-jnanakshi-500"
                >
                  <span className="font-medium">Q{index + 1}:</span> {qa.question}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-jnanakshi-500 hover:bg-jnanakshi-600 text-white rounded-full p-3 sm:p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95"
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? (
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>
    </div>
  );
};

export default Chatbot;
